import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../utils/apiClient';
import i18n, { setLocale } from '../i18n';
import { matchLocale, resolveLocale } from '../i18n/registry';
import { setFormattingTimezone } from '../utils/timezoneUtils';

const initialPreferences = () => ({
  ui_locale: resolveLocale(i18n.language, 'ui'),
  default_audio_language: 'auto',
  default_document_language: resolveLocale(i18n.language, 'documents'),
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
});
const validPreferences = saved => saved && matchLocale(saved.ui_locale, 'ui') &&
  (saved.default_audio_language === 'auto' || matchLocale(saved.default_audio_language, 'audio')) &&
  matchLocale(saved.default_document_language, 'documents');
const PreferencesContext = createContext({ preferences: initialPreferences(), updatePreferences: async () => {} });
export const usePreferences = () => useContext(PreferencesContext);

export function PreferencesProvider({ authenticated, children }) {
  const [preferences, setPreferences] = useState(initialPreferences);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const updating = useRef(false);
  const lifetime = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    updating.current = false;
    setSaving(false);
    setReady(false);
    setError('');
    if (authenticated) {
      (async () => {
        const response = await apiFetch('/auth/session', { credentials: 'include', signal: controller.signal });
        if (!response.ok) throw new Error();
        const data = await response.json();
        const saved = data.preferences || data;
        if (!validPreferences(saved)) throw new Error();
        if (controller.signal.aborted) return;
        const applied = await setLocale(saved.ui_locale, { signal: controller.signal });
        if (!applied || controller.signal.aborted) return;
        setPreferences(previous => ({ ...previous, ...saved, ui_locale: applied }));
        setFormattingTimezone(saved.timezone);
        setReady(true);
      })().catch(() => { if (!controller.signal.aborted) setError('preferences.loadError'); });
    }
    return () => { controller.abort(); };
  }, [authenticated, reloadKey]);

  const updatePreferences = async changes => {
    if (updating.current || (authenticated && !ready)) return;
    updating.current = true;
    const controller = lifetime.current;
    const signal = controller?.signal;
    setError('');
    setSaving(true);
    try {
      let saved = changes;
      if (authenticated) {
        const response = await apiFetch('/api/preferences', {
          method: 'PATCH', credentials: 'include', signal, headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(changes),
        });
        if (!response.ok) throw new Error();
        const data = await response.json();
        saved = data.preferences || data;
        if (!validPreferences(saved)) throw new Error();
      }
      if (signal?.aborted) return;
      if (saved.ui_locale) {
        const applied = await setLocale(saved.ui_locale, { signal });
        if (!applied) return;
        saved = { ...saved, ui_locale: applied };
      }
      if (signal?.aborted) return;
      setPreferences(previous => ({ ...previous, ...saved }));
      if (saved.timezone) setFormattingTimezone(saved.timezone);
    } catch (_) {
      if (!signal?.aborted) setError('preferences.saveError');
    } finally {
      if (controller === lifetime.current) {
        updating.current = false;
        if (!signal?.aborted) setSaving(false);
      }
    }
  };
  return <PreferencesContext.Provider value={{ preferences, updatePreferences, error, saving, ready, authenticated, retry: () => {
    setReady(false);
    setReloadKey(value => value + 1);
  } }}>
    {children}
  </PreferencesContext.Provider>;
}
